import { useEffect, useState } from 'react';
import {
  Users as UsersIcon, Plus, Loader2, Trash2, Edit3, UserPlus, UserCog,
  ExternalLink, FileDown,
} from 'lucide-react';
import {
  fetchAdminGroups, fetchAdminGroup,
  createAdminGroup, updateAdminGroup, deleteAdminGroup,
  groupInvoicesCsvPath, groupReportPdfPath,
  type GroupRow, type GroupDetailView,
} from '@/lib/api';
import { useAuthedDownload } from '@/hooks/useAuthedDownload';
import { Modal } from '@/components/ui/Modal';
import { UserTypeahead } from '@/components/ui/UserTypeahead';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './GroupsPage.module.css';

/**
 * /groups — Customer Groups admin surface.
 *
 * Lists tenant-scoped customer groups (named segments of
 * driver-tier users), with create + edit + delete via modals.
 * Membership is authored through the existing UserTypeahead
 * component (used elsewhere for charger guest pass list and
 * RFID assignments) — that helper's `searchUsersForRfid` source
 * already defaults to `role: 'driver'`, so we get driver-only
 * scope without changes.
 *
 * v2 base scope is segments + names + member lists. Future
 * integrations (per-group tariff, coupon distribution, comms
 * broadcast) are filed in
 * docs/tickets/group-tariff-coupon-comms.md and triggered by
 * v1 migration complaints rather than preemptive build.
 */

type ModalState =
  | { mode: 'create' }
  | { mode: 'edit'; groupId: string }
  | null;

export function GroupsPage() {
  useRecentRoute('Customer Groups');
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState<ModalState>(null);

  const reload = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      setGroups(await fetchAdminGroups());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><UsersIcon size={18} /></div>
          <div>
            <h1 className={styles.title}>Customer Groups</h1>
            <p className={styles.subtitle}>
              {groups.length} group{groups.length === 1 ? '' : 's'} —
              {' '}named segments of driver users for filtering and reporting
            </p>
          </div>
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => setModal({ mode: 'create' })}
        >
          <Plus size={15} /> New group
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && groups.length === 0 ? (
        <div className={styles.loading}>
          <Loader2 size={18} className={styles.spin} /> Loading…
        </div>
      ) : groups.length === 0 ? (
        <div className={styles.emptyState}>
          <UsersIcon size={28} />
          <p className={styles.emptyTitle}>No customer groups yet.</p>
          <p>Create a group to segment drivers for filtering and reporting.</p>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => setModal({ mode: 'create' })}
          >
            <Plus size={15} /> Create your first group
          </button>
        </div>
      ) : (
        <div className={styles.cardGrid}>
          {groups.map((g) => (
            <button
              key={g._id}
              type="button"
              className={styles.card}
              onClick={() => setModal({ mode: 'edit', groupId: g._id })}
            >
              <div className={styles.cardName}>{g.name}</div>
              <div className={`${styles.cardDesc} ${!g.description ? styles.cardDescEmpty : ''}`}>
                {g.description || 'No description.'}
              </div>
              <div className={styles.cardFooter}>
                <span className={styles.memberCount}>
                  <UsersIcon size={12} /> {g.member_count} member{g.member_count === 1 ? '' : 's'}
                </span>
                <span className={styles.editLink}>
                  <Edit3 size={12} /> Edit
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {modal?.mode === 'create' && (
        <GroupCreateModal
          onClose={() => setModal(null)}
          onCreated={() => {
            setModal(null);
            void reload();
          }}
        />
      )}
      {modal?.mode === 'edit' && (
        <GroupEditModal
          groupId={modal.groupId}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}

// ── Create modal ───────────────────────────────────────────────────

interface GroupCreateModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function GroupCreateModal({ onClose, onCreated }: GroupCreateModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    setError('');
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    try {
      await createAdminGroup({
        name: name.trim(),
        description: description.trim(),
        driver_user_ids: memberIds,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
      setSaving(false);
    }
  };

  return (
    <Modal isOpen title="New customer group" onClose={onClose} size="md">
      <div className={styles.modalBody}>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Name</label>
          <input
            className={formStyles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. VIP, Building A residents, Pilot drivers"
            autoFocus
            disabled={saving}
            maxLength={100}
          />
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Description (optional)</label>
          <textarea
            className={formStyles.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this group is for — used in reports and filters."
            disabled={saving}
            rows={2}
            maxLength={500}
          />
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Initial members (optional)</label>
          <UserTypeahead
            selectedUserIds={memberIds}
            onChange={setMemberIds}
            placeholder="Search drivers by name, email, or username…"
            disabled={saving}
            ariaLabel="Add drivers to group"
          />
          <p className={styles.modalHint}>
            Only drivers (PWA users) can be added. You can add or
            remove members anytime after creating the group.
          </p>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.modalActions}>
          <span />
          <div className={styles.modalActionsRight}>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={formStyles.btnPrimary}
              onClick={() => void submit()}
              disabled={saving}
            >
              {saving
                ? <><Loader2 size={13} className={styles.spin} /> Creating…</>
                : <><UserPlus size={13} /> Create group</>}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Edit modal ─────────────────────────────────────────────────────

interface GroupEditModalProps {
  groupId: string;
  onClose: () => void;
  onSaved: () => void;
}

function GroupEditModal({ groupId, onClose, onSaved }: GroupEditModalProps) {
  const [detail, setDetail] = useState<GroupDetailView | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  // Export CSV / Download PDF: fetched with the signed-in token and saved
  // (plain links reached the Api without it and got a 401).
  const downloads = useAuthedDownload();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    void fetchAdminGroup(groupId)
      .then((g) => {
        if (cancelled) return;
        setDetail(g);
        setName(g.name);
        setDescription(g.description);
        setMemberIds(g.members.map((m) => m._id));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load group');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [groupId]);

  const submit = async (): Promise<void> => {
    if (!detail) return;
    setError('');
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const body: { name?: string; description?: string; driver_user_ids?: string[] } = {};
    if (name.trim() !== detail.name) body.name = name.trim();
    if (description.trim() !== detail.description) body.description = description.trim();
    const previousIds = detail.members.map((m) => m._id);
    const sameMembers =
      memberIds.length === previousIds.length
      && memberIds.every((id, i) => id === previousIds[i]);
    if (!sameMembers) body.driver_user_ids = memberIds;
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await updateAdminGroup(groupId, body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!detail) return;
    if (!confirm(
      `Delete "${detail.name}"? This unlinks all ${detail.member_count} member${detail.member_count === 1 ? '' : 's'} from the group. The drivers themselves are unaffected.`,
    )) return;
    setDeleting(true);
    setError('');
    try {
      await deleteAdminGroup(groupId);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Modal isOpen title="Loading group…" onClose={onClose} size="md">
        <div className={styles.loading}>
          <Loader2 size={18} className={styles.spin} /> Loading…
        </div>
      </Modal>
    );
  }

  if (!detail) {
    return (
      <Modal isOpen title="Error" onClose={onClose} size="md">
        <div className={styles.error}>{error || 'Group not found'}</div>
      </Modal>
    );
  }

  const busy = saving || deleting;

  return (
    <Modal isOpen title={`Edit ${detail.name}`} onClose={onClose} size="md">
      <div className={styles.modalBody}>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Name</label>
          <input
            className={formStyles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            maxLength={100}
            autoFocus
          />
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Description</label>
          <textarea
            className={formStyles.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this group is for."
            disabled={busy}
            rows={2}
            maxLength={500}
          />
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>
            Members ({memberIds.length})
          </label>
          <UserTypeahead
            selectedUserIds={memberIds}
            onChange={setMemberIds}
            placeholder="Search drivers to add or remove…"
            disabled={busy}
            ariaLabel="Manage group members"
          />
          <p className={styles.modalHint}>
            Removing a driver from the group doesn&rsquo;t deactivate
            their account — just unlinks them from this segment.
          </p>
        </div>

        {error && <div className={styles.error}>{error}</div>}
        {downloads.error && <div className={styles.error} role="alert">{downloads.error}</div>}

        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.dangerBtn}
            onClick={() => void handleDelete()}
            disabled={busy}
          >
            {deleting
              ? <><Loader2 size={13} className={styles.spin} /> Deleting…</>
              : <><Trash2 size={13} /> Delete group</>}
          </button>
          <div className={styles.modalActionsRight}>
            {/* Export options — two distinct buttons, mirroring
                the driver detail page's pattern (sub-phase 6c).
                "Export CSV" for raw data → spreadsheet pipeline;
                "Download PDF" for a finished document → email/
                print/share. Different verbs telegraph format
                intent at the label level. Both endpoints accept
                optional date range; UI sends none for v1 (full
                history, 10000-row server cap). The PDF report
                aggregates across every member of the group with
                per-row driver attribution and a single grand-
                total. Group "detail" surface IS this modal —
                no full-page route, so the export triggers live
                here alongside the save action. Buttons, not links:
                the file is fetched with the signed-in token. */}
            <button
              type="button"
              className={styles.exportBtn}
              disabled={downloads.isBusy('csv')}
              aria-busy={downloads.isBusy('csv')}
              onClick={() => void downloads.download('csv', groupInvoicesCsvPath(groupId), `invoices-group-${groupId}.csv`)}
            >
              {downloads.isBusy('csv') ? <Loader2 size={12} className={styles.spin} /> : <ExternalLink size={12} />} Export CSV
            </button>
            <button
              type="button"
              className={styles.exportBtn}
              disabled={downloads.isBusy('pdf')}
              aria-busy={downloads.isBusy('pdf')}
              onClick={() => void downloads.download('pdf', groupReportPdfPath(groupId), `report-group-${groupId}.pdf`)}
            >
              {downloads.isBusy('pdf') ? <Loader2 size={12} className={styles.spin} /> : <FileDown size={12} />} Download PDF
            </button>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={formStyles.btnPrimary}
              onClick={() => void submit()}
              disabled={busy}
            >
              {saving
                ? <><Loader2 size={13} className={styles.spin} /> Saving…</>
                : <><UserCog size={13} /> Save changes</>}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
